import { api } from "../api";
import { el, clear, copyText, registerBlobUrl } from "../dom";
import {
  beginJoinKeys,
  clearJoinKeys,
  loadIdentity,
  readAdminHash,
  storeAdmin,
  stripAdminHash,
  type Identity,
} from "../identity";
import { connectLive } from "../live";
import { startSaver } from "../saver";
import {
  buildGridModel,
  mountHeatGrid,
  mountPager,
  mountPaintGrid,
  pageDates,
  perPageFor,
} from "../grid";
import { mountOrganizer, type OrgHandle } from "./organizer";
import {
  acknowledgePrune,
  applyDelta,
  applyState,
  asStateWrap,
  beginJoin,
  edit,
  initial,
  joinNeedsFreshKeys,
  view as syncView,
  type Sync,
} from "../../shared/sync";
import { buildIcs, googleCalendarUrl } from "../../shared/ics";
import { freeCells } from "../../shared/lunacal";
import { rankWindows, tallyPoll } from "../../shared/score";
import { slotStarts, zonedParts } from "../../shared/slots";
import {
  deadlineLabel,
  formatUpdated,
  tiedLeaders,
  unmatchedAnswers,
  viewerZoneCollides,
  waitingOn,
  windowLabel,
  STATUS_COPY,
  shouldAssignNote,
} from "../../shared/present";
import { isRecord, randomChars } from "../../shared/http";
import type { EventState, Level, Locked, PollVote } from "../../shared/types";
import type { WindowScore } from "../../shared/score";
import type { GridModel } from "../../shared/paint";

const BRUSHES: { level: Level; label: string; testid: string }[] = [
  { level: 3, label: "Preferred", testid: "brush-3" },
  { level: 2, label: "Works", testid: "brush-2" },
  { level: 1, label: "If need be", testid: "brush-1" },
  { level: 0, label: "Erase", testid: "brush-0" },
];

/** Delays before re-checking an admin token that could not be verified the first time (a 0, 429,
 * or 5xx on the /log check): 2s, 5s, 15s, then every 30s while the view stays mounted. */
const ADMIN_RECHECK_DELAYS = [2000, 5000, 15000];

function cssPx(name: string): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

function ensureButton(parent: HTMLElement, found: Element | null, make: () => HTMLButtonElement): HTMLButtonElement {
  if (found instanceof HTMLButtonElement) return found;
  const created = make();
  parent.append(created);
  return created;
}

/** A question that already ends in "?" or ":" is not followed by another colon. */
function pollLine(question: string, label: string): string {
  const sep = /[?:]$/.test(question) ? "" : ":";
  return `${question}${sep} ${label}`;
}

function lockedWhere(v: EventState, locked: Locked): string {
  const labels: string[] = [];
  for (const poll of v.event.polls) {
    const winId = locked.pollWinners[poll.id];
    const label = poll.options.find((o) => o.id === winId)?.label;
    if (label) labels.push(label);
  }
  return labels.join(" · ");
}

function asAdminCheckOk(v: unknown): true | null {
  return isRecord(v) ? true : null;
}

interface FillSlots {
  slots: number[];
  slotLenMin: number;
}

function asFillSlots(v: unknown): FillSlots | null {
  if (!isRecord(v)) return null;
  if (!Array.isArray(v.slots) || typeof v.slotLenMin !== "number") return null;
  const slots: number[] = [];
  for (const n of v.slots) {
    if (typeof n !== "number") return null;
    slots.push(n);
  }
  return { slots, slotLenMin: v.slotLenMin };
}

export function renderEvent(root: HTMLElement, id: string): { destroy(): void } {
  const box: { current: () => void } = { current: () => {} };
  void loadAndMount(root, id, box);
  return { destroy: () => box.current() };
}

interface AdminMeta {
  adminInvalid: boolean;
  organizerPersisted: boolean | null;
  /** A hash that could not be verified yet (a transient failure), held only for this mount: never
   * localStorage, never the identity memory map, so an unchecked token can never replace a good
   * stored one. */
  unverifiedAdmin: string | null;
}

async function loadAndMount(root: HTMLElement, id: string, box: { current: () => void }): Promise<void> {
  root.replaceChildren(el("div", { class: "wrap" }, el("p", { class: "muted", text: "Loading poll" })));
  const hash = readAdminHash();
  const storedAdmin = loadIdentity(id).adminToken;
  const meta: AdminMeta = { adminInvalid: false, organizerPersisted: null, unverifiedAdmin: null };
  // Every strip that is not backed by a persisted token waits until the view is about to mount.
  let stripOnMount = false;
  if (hash !== null && hash === storedAdmin) {
    // Already the token this poll trusts, so there is nothing to verify. But `storedAdmin` can be
    // the in-memory copy of a token storage refused (the "Try again" path after a first load with
    // storage blocked), so ask storage again instead of assuming. If storage holds it, strip now,
    // because a reload reads it back. If it lives in memory alone, the hash is still the one way
    // back to it, so it stays until the view has mounted and can offer "Copy organizer link".
    meta.organizerPersisted = storeAdmin(id, hash);
    if (meta.organizerPersisted) stripAdminHash();
    else stripOnMount = true;
  }
  const shouldCheck = hash !== null && hash !== storedAdmin;
  const [adminCheck, got] = await Promise.all([
    shouldCheck ? api("GET", `/api/events/${id}/log`, { adminToken: hash, parse: asAdminCheckOk }) : Promise.resolve(null),
    api<{ state: EventState }>("GET", `/api/events/${id}`, { parse: asStateWrap }),
  ]);
  // When storage held the token, stripping now is safe even if the state fetch below fails: a
  // retry or a reload reads it straight back out of storage. When storage could not hold it
  // (blocked), the hash in the address bar is the ONLY way back to it until the event view has
  // actually mounted, so it has to survive a page-level failure screen below; it is stripped only
  // once mounting is about to happen.
  if (shouldCheck && hash !== null) {
    if (adminCheck && adminCheck.ok) {
      meta.organizerPersisted = storeAdmin(id, hash);
      if (meta.organizerPersisted) stripAdminHash();
      else stripOnMount = true;
    } else if (adminCheck && !adminCheck.ok && adminCheck.status === 401) {
      stripAdminHash();
      meta.adminInvalid = true;
    } else {
      // 0, 429, 5xx, or an unparsable shape: this is not an answer. The hash stays in the address
      // bar (a "Try again" on a page-level failure below must still see it) and the token lives
      // only in this view's own memory until a retry settles it.
      meta.unverifiedAdmin = hash;
    }
  }
  if (!got.ok && got.status === 404) {
    showNotFound(root);
    box.current = () => {};
    return;
  }
  if (!got.ok) {
    // Page-level failure: never strip here. A hash held only because storage was blocked must
    // still be in the address bar for a "Try again" click, or even a plain reload, to recover it.
    showFail(root, got.error, () => {
      void loadAndMount(root, id, box);
    });
    box.current = () => {};
    return;
  }
  const handle = mountEvent(root, id, got.data.state, meta);
  box.current = handle.destroy;
  // After mounting, not before: if mounting threw, the hash would already be gone and nothing on
  // screen could show the token it carried.
  if (stripOnMount) stripAdminHash();
}

export function showNotFound(root: HTMLElement): void {
  root.replaceChildren(
    el(
      "div",
      { class: "wrap" },
      el("p", {
        class: "error",
        "data-testid": "page-error",
        text: "This poll was not found. Polls are deleted 120 days after their last date.",
      }),
      el("p", null, el("a", { href: "/", text: "Start a new poll" })),
    ),
  );
}

function showFail(root: HTMLElement, message: string, retry: () => void): void {
  const btn = el("button", { type: "button", text: "Try again" });
  btn.addEventListener("click", retry);
  root.replaceChildren(
    el("div", { class: "wrap" }, el("p", { class: "error", "data-testid": "page-error", text: message }), el("p", null, btn)),
  );
}

function mountEvent(root: HTMLElement, id: string, first: EventState, meta: AdminMeta): { destroy(): void } {
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const tabOrigin = randomChars(8);
  let ident: Identity = loadIdentity(id);
  let sync: Sync = applyState(initial(ident.pid, tabOrigin), first);
  let brush: Level = 3;
  let useOrganizerTz = false;
  let filterPid: string | null = null;
  let page = 0;
  let lockSlot: number | null = null;
  let containerWidth = 320;
  let resizeRaf = 0;
  let clearedForGone = false;
  let editPersisted = true;
  let organizerPersisted = meta.organizerPersisted;
  let shareCopyTimer: ReturnType<typeof setTimeout> | null = null;

  // A hash that failed to verify yet (see loadAndMount): held only here, never persisted. Once the
  // retry loop below settles it, this returns to null either way (stored, or definitively invalid).
  let unverifiedAdmin: string | null = meta.unverifiedAdmin;
  let adminRecheckTimer: ReturnType<typeof setTimeout> | null = null;
  let adminRecheckN = 0;
  // Set once destroy() runs. A recheck's own request can still be in flight at that point; this
  // guards its resolution from touching storage, the DOM, or the timer on a torn-down view.
  let destroyed = false;

  function adminToken(): string | null {
    return ident.adminToken ?? unverifiedAdmin;
  }

  let gridModelMemo: { key: string; model: GridModel } | null = null;
  function getGridModel(v: EventState, tz: string): GridModel {
    const key = `${v.event.timezone}|${v.event.dates.join(",")}|${v.event.dayStartMin}|${v.event.dayEndMin}|${tz}`;
    if (gridModelMemo && gridModelMemo.key === key) return gridModelMemo.model;
    const model = buildGridModel(v.event, tz);
    gridModelMemo = { key, model };
    return model;
  }

  // Keyed on rev AND editSeq: my own unsaved paint changes editSeq without changing rev, and the
  // ranking is computed over the draft-overlaid view so it reflects that paint immediately, even
  // offline.
  let rankMemo: { rev: number; editSeq: number; ranked: WindowScore[] } | null = null;
  function getRanked(v: EventState): WindowScore[] {
    if (rankMemo && rankMemo.rev === v.rev && rankMemo.editSeq === sync.editSeq) return rankMemo.ranked;
    const overlaid = syncView(sync) ?? v;
    const ranked = rankWindows(overlaid, 5);
    rankMemo = { rev: v.rev, editSeq: sync.editSeq, ranked };
    return ranked;
  }

  // On the viewer's own fall-back night, two different UTC instants read as the same date and
  // clock time in the viewer's zone, so the grid can only paint half of what is really there. When
  // that happens the organizer's zone is used instead (it never collides with itself) and the
  // toggle is hidden, since there is nothing safe to toggle to.
  let collidesMemo: { key: string; collides: boolean } | null = null;
  function forcedOrgTz(v: EventState): boolean {
    const key = `${v.event.timezone}|${v.event.dates.join(",")}|${v.event.dayStartMin}|${v.event.dayEndMin}|${viewerTz}`;
    if (collidesMemo && collidesMemo.key === key) return collidesMemo.collides;
    const collides = viewerZoneCollides(slotStarts(v.event), viewerTz);
    collidesMemo = { key, collides };
    return collides;
  }

  const wrap = el("div", { class: "wrap event-page" });

  const eventTitle = el("h1", { "data-testid": "event-title" });
  const eventNote = el("p", { class: "muted", "data-testid": "event-note" });
  const eventDeadline = el("p", { class: "muted", "data-testid": "event-deadline" });
  const eventHead = el("div", { class: "event-head" }, eventTitle, eventNote, eventDeadline);

  const adminInvalidNotice = el("p", {
    class: "warn",
    "data-testid": "admin-invalid",
    text: "That organizer link is not valid for this poll.",
  });
  adminInvalidNotice.hidden = !meta.adminInvalid;

  const adminUnverifiedNotice = el("p", {
    class: "warn",
    "data-testid": "admin-unverified",
    text: "Could not check your organizer link just now. Trying again.",
  });
  adminUnverifiedNotice.hidden = unverifiedAdmin === null;

  const storageWarning = el("div", { "data-testid": "storage-warning" });
  storageWarning.hidden = true;

  const shareUrl = `${location.origin}/e/${id}`;
  const shareInput = el("input", { type: "text", readOnly: true, "data-testid": "share-url" });
  shareInput.value = shareUrl;
  const shareCopy = el("button", { type: "button", text: "Copy link", "data-testid": "share-copy" });
  shareCopy.addEventListener("click", () => {
    void (async () => {
      const result = await copyText(shareUrl, shareInput);
      shareCopy.textContent = result === "copied" ? "Copied" : "Press Ctrl+C to copy";
      if (shareCopyTimer) clearTimeout(shareCopyTimer);
      shareCopyTimer = setTimeout(() => {
        shareCopy.textContent = "Copy link";
      }, 2000);
    })();
  });
  const shareBox = el("div", { class: "share-box" }, shareInput, shareCopy);
  if (typeof navigator.share === "function") {
    const sh = el("button", { type: "button", text: "Share" });
    sh.addEventListener("click", () => {
      void navigator.share({ url: shareUrl, title: first.event.title }).catch(() => {
        /* cancelled */
      });
    });
    shareBox.append(sh);
  }

  const saveStatus = el("p", { class: "label", "data-testid": "save-status", "aria-live": "polite" });
  const liveStatus = el("p", { class: "label", "data-testid": "live-status" });
  const statusBox = el("div", { class: "status-box" }, saveStatus, liveStatus);
  const prunedNote = el("p", { class: "pruned-note", "data-testid": "pruned-note" });
  prunedNote.hidden = true;

  const lockedBanner = el("div", { class: "banner full-span", "data-testid": "locked-banner" });
  lockedBanner.hidden = true;

  const joinMessage = el("p", { class: "muted", "data-testid": "join-message" });
  const joinName = el("input", { type: "text", maxlength: "40", "data-testid": "join-name" });
  const joinSubmit = el("button", { class: "primary", type: "button", text: "Join", "data-testid": "join-submit" });
  const joinBox = el(
    "div",
    { class: "join-box full-span" },
    joinMessage,
    el("label", { class: "field" }, el("span", { class: "label", text: "Your name" }), joinName),
    joinSubmit,
  );

  const pagerNav = el("div", { class: "pager full-span" });
  const tzNotice = el("p", { class: "muted", "data-testid": "tz-notice" });
  const tzForcedNotice = el("p", { class: "notice", "data-testid": "tz-forced" });
  tzForcedNotice.hidden = true;
  const tzToggle = el("button", { type: "button", text: "Show in organizer timezone", "data-testid": "tz-toggle" });
  tzToggle.addEventListener("click", () => {
    useOrganizerTz = !useOrganizerTz;
    gridModelMemo = null;
    updateAll();
  });
  const gridInfo = el("div", { class: "grid-info full-span" }, tzNotice, tzForcedNotice, tzToggle);

  const brushBar = el("div", { class: "brush-bar" });
  const brushBtns: HTMLButtonElement[] = [];
  for (const b of BRUSHES) {
    const btn = el("button", {
      type: "button",
      text: b.label,
      "data-testid": b.testid,
      "aria-pressed": b.level === brush ? "true" : "false",
    });
    btn.addEventListener("click", () => {
      brush = b.level;
      for (const x of brushBtns) x.setAttribute("aria-pressed", x === btn ? "true" : "false");
    });
    brushBar.append(btn);
    brushBtns.push(btn);
  }

  const paintTable = el("table", { class: "grid", "data-testid": "paint-grid" });
  const lockedNote = el("p", {
    class: "muted locked-note",
    "data-testid": "locked-note",
    text: "This time is locked. Painting is closed.",
  });
  lockedNote.hidden = true;
  const touchHint = el("p", {
    class: "muted",
    "data-testid": "touch-hint",
    text: "Drag across the grid to paint. Scroll from the time labels or outside the grid.",
  });
  const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  touchHint.hidden = !isTouch;

  const noteInput = el("textarea", { maxlength: "120", "data-testid": "note-input" });
  noteInput.addEventListener("input", () => {
    sync = edit(sync, (d) => ({ ...d, note: noteInput.value }));
    saver.bump();
    updateAll();
  });
  const fillBtn = el("button", { type: "button", text: "Fill from my Lunacal", "data-testid": "fill-lunacal" });
  const fillMsg = el("p", { class: "muted", "data-testid": "fill-message" });
  fillBtn.addEventListener("click", () => {
    void fillLunacal();
  });

  // paintBox and groupBox each carry three stacked pieces (heading, controls, body). Wrapped in
  // .avail-row, a subgrid keeps row 2 (brush bar vs. legend+chips) the same height on both sides,
  // so row 3 -- the actual grid table -- starts at the same Y in both columns regardless of how
  // many lines the legend text wraps to. See the matching CSS in public/app.css.
  const paintBox = el(
    "section",
    { class: "paint-col" },
    el("h2", { text: "Your availability" }),
    el("div", { class: "section-controls" }, brushBar),
    el(
      "div",
      { class: "section-body" },
      paintTable,
      lockedNote,
      touchHint,
      el("label", { class: "field" }, el("span", { class: "label", text: "Note" }), noteInput),
      fillBtn,
      fillMsg,
    ),
  );

  const heatTable = el("table", { class: "grid", "data-testid": "heat-grid" });
  const legend = el("p", {
    class: "legend",
    text: "Brighter means more people can make it. A dark ring means everyone who answered can.",
  });
  const chips = el("div", { class: "chips" });
  const hover = el("div", { class: "hover-list" });
  hover.hidden = true;
  const groupBox = el(
    "section",
    { class: "group-col" },
    el("h2", { text: "Group" }),
    el("div", { class: "section-controls" }, legend, chips),
    el("div", { class: "section-body" }, heatTable, hover),
  );
  const availRow = el("div", { class: "avail-row full-span" }, paintBox, groupBox);

  const waitingHeading = el("h2", { text: "Waiting on" });
  const waitingLine = el("p", { class: "muted" });
  const waitingList = el("ul", { class: "plain", "data-testid": "waiting-list" });
  const unmatchedLine = el("p", { class: "muted", "data-testid": "unmatched-line", hidden: true });
  const bestList = el("div", { "data-testid": "best-list" });
  const lockPanel = el("div", { "data-testid": "lock-panel" });
  lockPanel.hidden = true;
  const bestExplain = el("p", {
    class: "muted",
    "data-testid": "best-explain",
    text: "Ranked by fewest required people missing, then most people free, then strongest preference.",
  });
  const bestBox = el(
    "section",
    { class: "best-col" },
    el("h2", { text: "Best times" }),
    bestExplain,
    waitingLine,
    bestList,
    lockPanel,
  );

  const pollsExplain = el("p", {
    class: "muted",
    "data-testid": "polls-explain",
    text: "The option most people can do wins. Love it breaks a tie.",
  });
  const pollsBox = el("section", { class: "polls-col" }, el("h2", { text: "Polls" }), pollsExplain);

  const peopleList = el("ul", { class: "plain", "data-testid": "people-list" });
  const peopleBox = el(
    "section",
    { class: "people-col full-span" },
    el("h2", { text: "People" }),
    peopleList,
    waitingHeading,
    waitingList,
    unmatchedLine,
  );

  const orgDetails = el("details", { "data-testid": "organizer-panel", class: "full-span" });

  const layout = el(
    "div",
    { class: "event-layout" },
    lockedBanner,
    joinBox,
    pagerNav,
    gridInfo,
    availRow,
    bestBox,
    pollsBox,
    peopleBox,
    orgDetails,
  );
  const panel = el("div", { class: "panel" }, layout);

  wrap.append(eventHead, adminInvalidNotice, adminUnverifiedNotice, storageWarning, shareBox, statusBox, prunedNote, panel);
  root.replaceChildren(wrap);

  // The grid tables live in .panel's content box, not .wrap's: .wrap's own clientWidth includes
  // .panel's horizontal padding and border, which are not space the table actually has. Measuring
  // .wrap alone over-reports by that padding, so at narrow widths columnsPerPage was handed a wider
  // number than the table could really use and the grid ran past the panel's edge.
  function measureContainerWidth(): number {
    const cs = getComputedStyle(panel);
    const pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    const inner = panel.clientWidth - pad;
    return inner > 0 ? inner : wrap.clientWidth || window.innerWidth || 320;
  }
  containerWidth = measureContainerWidth();

  const getSync = () => sync;
  const paintGrid = mountPaintGrid(paintTable, {
    getSync,
    getBrush: () => brush,
    getBottomInset() {
      if (brushBar.hidden) return 0;
      if (getComputedStyle(brushBar).position !== "fixed") return 0;
      return brushBar.getBoundingClientRect().height;
    },
    onPaint(availability) {
      sync = edit(sync, (d) => ({ ...d, availability }));
      saver.bump();
      updateAll();
    },
  });
  const heatGrid = mountHeatGrid(heatTable, {
    onInspect(slot, cell) {
      showHover(slot, cell);
    },
  });
  const pager = mountPager(pagerNav, (p) => {
    page = p;
    updateAll();
  });

  const saver = startSaver({
    eventId: id,
    getSync,
    setSync(s) {
      sync = s;
    },
    getTokens() {
      if (!ident.pid || !ident.editToken) return null;
      return { pid: ident.pid, editToken: ident.editToken };
    },
    onUpdate() {
      updateAll();
    },
    onError(err) {
      console.error(err);
    },
  });

  const live = connectLive({
    eventId: id,
    onMessage(msg) {
      const out = applyDelta(sync, msg);
      if (out.refetch) {
        void refetch();
        return;
      }
      sync = out.sync;
      updateAll();
    },
    onStatus(st) {
      liveStatus.textContent = st === "live" ? "Live" : "Reconnecting";
    },
    onNotFound() {
      live.stop();
      showNotFound(root);
    },
    refetch,
  });

  let org: OrgHandle | null = adminToken()
    ? mountOrganizer(orgDetails, {
        eventId: id,
        getAdmin: () => adminToken(),
        onState(state) {
          sync = applyState(sync, state);
          updateAll();
        },
        onNotice() {
          /* organizer.ts already renders this inside its own notice line */
        },
      })
    : null;

  function scheduleAdminRecheck(): void {
    if (adminRecheckTimer) clearTimeout(adminRecheckTimer);
    const wait = ADMIN_RECHECK_DELAYS[adminRecheckN] ?? 30_000;
    if (adminRecheckN < ADMIN_RECHECK_DELAYS.length) adminRecheckN += 1;
    adminRecheckTimer = setTimeout(() => {
      void recheckAdmin();
    }, wait);
  }

  async function recheckAdmin(): Promise<void> {
    const token = unverifiedAdmin;
    if (!token) return;
    const res = await api("GET", `/api/events/${id}/log`, { adminToken: token, parse: asAdminCheckOk });
    // Clearing the timer in destroy() does not stop a request that already left: a view torn down
    // (route change, another view mounted) while this check was in flight must not touch storage,
    // the DOM, or re-arm a timer on a page nobody is looking at anymore.
    if (destroyed) return;
    if (res.ok) {
      unverifiedAdmin = null;
      organizerPersisted = storeAdmin(id, token);
      // adminToken() prefers ident.adminToken over the now-cleared unverifiedAdmin: without
      // refreshing ident here, the organizer tools this view already turned on would vanish the
      // instant the check that was supposed to confirm them actually succeeds.
      ident = loadIdentity(id);
      stripAdminHash();
      adminUnverifiedNotice.hidden = true;
      updateAll();
      return;
    }
    if (res.status === 401) {
      unverifiedAdmin = null;
      stripAdminHash();
      adminUnverifiedNotice.hidden = true;
      adminInvalidNotice.hidden = false;
      org?.destroy();
      org = null;
      updateAll();
      return;
    }
    scheduleAdminRecheck();
  }

  if (unverifiedAdmin) scheduleAdminRecheck();

  joinSubmit.addEventListener("click", () => {
    const name = joinName.value.trim();
    if (!name) {
      joinMessage.textContent = "Name is required.";
      return;
    }
    if (joinNeedsFreshKeys(sync)) {
      // The current identity cannot possibly work (gone, or never joined): mint a fresh pid and
      // edit token, discarding whatever was stored before, in every state that reaches this branch.
      const keys = beginJoinKeys(id);
      editPersisted = keys.persisted;
      ident = loadIdentity(id);
      sync = beginJoin(sync, keys.pid, name);
    } else {
      // A retry of a join whose answer was lost (still "retrying" or "error" with a draft): reuse
      // the same pid and token and just set the name, which the server treats as the same person
      // whether or not the first request actually landed. Minting fresh keys here would leave the
      // first row on the server under this name, taken by this tab's own ghost.
      sync = edit(sync, (d) => ({ ...d, name }));
    }
    saver.bump();
    updateAll();
  });

  document.addEventListener("keydown", onKey);
  document.addEventListener("pointerdown", onPointerDown);
  window.addEventListener("resize", scheduleResize);

  function onKey(e: KeyboardEvent): void {
    if (e.key === "Escape") hideHover();
  }
  function onPointerDown(e: PointerEvent): void {
    if (hover.hidden) return;
    if (hover.contains(e.target as Node)) return;
    hideHover();
  }

  function hideHover(): void {
    hover.hidden = true;
    clear(hover);
  }

  function showHover(slot: number, cell: HTMLTableCellElement): void {
    const v = sync.server;
    if (!v) return;
    const people = filterPid ? v.participants.filter((p) => p.id === filterPid) : v.participants;
    clear(hover);
    const inn: string[] = [];
    const out: string[] = [];
    for (const p of people) {
      const lv = p.availability[String(slot)] ?? 0;
      const bit = p.note ? `${p.name} (${p.note})` : p.name;
      if (lv >= 1) inn.push(bit);
      else out.push(p.name);
    }
    hover.append(el("p", { text: inn.length ? `In: ${inn.join(", ")}` : "In: nobody" }));
    hover.append(el("p", { text: out.length ? `Out: ${out.join(", ")}` : "Out: nobody" }));
    if (adminToken() && !v.locked) {
      const lockFrom = el("button", { type: "button", text: "Lock from this time" });
      lockFrom.addEventListener("click", () => {
        openLock(slot);
        hideHover();
      });
      hover.append(lockFrom);
    }
    hover.hidden = false;
    const rect = cell.getBoundingClientRect();
    hover.style.left = `${rect.left + window.scrollX}px`;
    hover.style.top = `${rect.bottom + window.scrollY}px`;
  }

  async function refetch(): Promise<void> {
    const res = await api<{ state: EventState }>("GET", `/api/events/${id}`, { parse: asStateWrap });
    if (!res.ok && res.status === 404) {
      live.stop();
      showNotFound(root);
      return;
    }
    if (!res.ok) return;
    sync = applyState(sync, res.data.state);
    updateAll();
  }

  async function fillLunacal(): Promise<void> {
    const v = sync.server;
    if (!v || v.locked || !adminToken()) return;
    const res = await api("GET", "/api/lunacal/slots", { parse: asFillSlots });
    if (!res.ok) {
      fillMsg.textContent = res.error;
      return;
    }
    const cells = freeCells(res.data.slots, slotStarts(v.event), v.event.slotMin, res.data.slotLenMin);
    let n = 0;
    sync = edit(sync, (d) => {
      const availability = { ...d.availability };
      for (const c of cells) {
        const k = String(c);
        if (!availability[k]) {
          availability[k] = 2;
          n += 1;
        }
      }
      return { ...d, availability };
    });
    saver.bump();
    let extra = "";
    if (res.data.slots.length) {
      const last = res.data.slots[res.data.slots.length - 1];
      if (last !== undefined) {
        const through = zonedParts(last, v.event.timezone).date;
        const maxDate = v.event.dates[v.event.dates.length - 1];
        if (maxDate && maxDate > through) extra = ` Lunacal lists openings through ${through}.`;
      }
    }
    fillMsg.textContent = `Filled ${n} cells.${extra}`;
    updateAll();
  }

  function viewTz(): string {
    const v = sync.server;
    if (v && forcedOrgTz(v)) return v.event.timezone;
    return useOrganizerTz ? v?.event.timezone ?? viewerTz : viewerTz;
  }

  function joined(): boolean {
    if (sync.status === "gone" || !sync.pid || !sync.draft) return false;
    return sync.server?.participants.some((p) => p.id === sync.pid) === true;
  }

  function isLockedNow(): boolean {
    return sync.server?.locked != null;
  }

  function currentLayout(v: EventState): { model: GridModel; dates: string[]; per: number } {
    const tz = viewTz();
    const model = getGridModel(v, tz);
    const timeCol = cssPx("--time-col");
    const cellMin = cssPx("--cell-min");
    const per = perPageFor(containerWidth, timeCol, cellMin, model.dates.length);
    const maxPage = Math.max(0, Math.ceil(model.dates.length / per) - 1);
    if (page > maxPage) page = maxPage;
    const dates = pageDates(model, page, per);
    return { model, dates, per };
  }

  function updateHeatGrid(): void {
    const v = sync.server;
    if (!v) return;
    const { model, dates, per } = currentLayout(v);
    pager.update(model.dates, page, per);
    heatGrid.update(model, dates, v.participants, filterPid);
  }

  function updatePaintGrid(): void {
    const v = sync.server;
    if (!v || !joined()) return;
    const { model, dates } = currentLayout(v);
    const avail = sync.draft?.availability ?? {};
    paintGrid.update(model, dates, avail);
  }

  function scheduleResize(): void {
    if (resizeRaf) return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0;
      containerWidth = measureContainerWidth();
      if (!paintGrid.isDragging()) updateAll();
    });
  }

  function measureBrushHeight(): void {
    const fixed = getComputedStyle(brushBar).position === "fixed" && !brushBar.hidden;
    document.documentElement.style.setProperty(
      "--brush-h",
      fixed ? `calc(${Math.ceil(brushBar.getBoundingClientRect().height)}px + env(safe-area-inset-bottom))` : "0px",
    );
  }

  function renderStorageWarning(): void {
    clear(storageWarning);
    if (ident.adminToken && organizerPersisted === false) {
      storageWarning.hidden = false;
      storageWarning.append(
        el("p", {
          text:
            "This browser is not saving your organizer key. Copy your private organizer link now, or you will lose control of this poll when you close this tab.",
        }),
      );
      const btn = el("button", { type: "button", text: "Copy organizer link" });
      btn.addEventListener("click", () => {
        void (async () => {
          const link = `${location.origin}/e/${id}#admin=${ident.adminToken ?? ""}`;
          const result = await copyText(link);
          btn.textContent = result === "copied" ? "Copied" : "Press Ctrl+C to copy";
          setTimeout(() => {
            btn.textContent = "Copy organizer link";
          }, 2000);
        })();
      });
      storageWarning.append(btn);
    } else if (sync.pid && !editPersisted) {
      storageWarning.hidden = false;
      storageWarning.append(
        el("p", { text: "This browser is not saving your edit key. Keep this tab open to change your answers." }),
      );
    } else {
      storageWarning.hidden = true;
    }
  }

  function updateHead(v: EventState): void {
    if (eventTitle.textContent !== v.event.title) eventTitle.textContent = v.event.title;
    document.title = `${v.event.title} · Overlap`;
    eventNote.hidden = !v.event.note;
    if (v.event.note) eventNote.textContent = v.event.note;
    if (v.event.deadline) {
      eventDeadline.hidden = false;
      eventDeadline.textContent = deadlineLabel(v.event.deadline, viewerTz);
    } else {
      eventDeadline.hidden = true;
    }
  }

  function updateGridInfo(v: EventState): void {
    if (forcedOrgTz(v)) {
      tzNotice.hidden = true;
      tzToggle.hidden = true;
      tzForcedNotice.hidden = false;
      tzForcedNotice.textContent = `Times are shown in the organizer's timezone (${v.event.timezone}), because your own clock repeats an hour on one of these days.`;
      return;
    }
    tzForcedNotice.hidden = true;
    if (viewerTz !== v.event.timezone) {
      tzNotice.hidden = false;
      tzNotice.textContent = `Times shown in ${viewTz()}. Organizer zone is ${v.event.timezone}.`;
      tzToggle.hidden = false;
      tzToggle.textContent = useOrganizerTz ? "Show in my timezone" : "Show in organizer timezone";
    } else {
      tzNotice.hidden = true;
      tzToggle.hidden = true;
    }
  }

  function updateStatus(): void {
    saveStatus.textContent = joined() ? STATUS_COPY[sync.status] : "";
    if (sync.prunedSticky > 0) {
      prunedNote.hidden = false;
      prunedNote.textContent = `${sync.prunedSticky} of your cells were on times the organizer removed.`;
    } else {
      prunedNote.hidden = true;
      prunedNote.textContent = "";
    }
  }

  function updateLocked(v: EventState): void {
    const locked = v.locked;
    if (!locked) {
      lockedBanner.hidden = true;
      clear(lockedBanner);
      brushBar.hidden = false;
      lockedNote.hidden = true;
      paintTable.removeAttribute("aria-disabled");
      return;
    }
    lockedBanner.hidden = false;
    clear(lockedBanner);
    brushBar.hidden = true;
    lockedNote.hidden = false;
    paintTable.setAttribute("aria-disabled", "true");
    const tz = viewTz();
    const end = locked.slotStart + locked.durationMin;
    lockedBanner.append(el("h2", { text: windowLabel(locked.slotStart, end, tz) }));
    const total = locked.available.length + locked.missing.length;
    lockedBanner.append(el("p", { text: `${locked.available.length} of ${total} can make it` }));
    if (locked.missing.length) lockedBanner.append(el("p", { text: `Missing: ${locked.missing.join(", ")}` }));
    for (const poll of v.event.polls) {
      const winId = locked.pollWinners[poll.id];
      const label = poll.options.find((o) => o.id === winId)?.label;
      if (label) lockedBanner.append(el("p", { text: pollLine(poll.question, label) }));
    }
    const where = lockedWhere(v, locked);
    const actions = el("div", { class: "banner-actions" });
    const icsBtn = el("button", { type: "button", text: "Add to calendar (.ics)", "data-testid": "ics-download" });
    icsBtn.addEventListener("click", () => {
      const ics = buildIcs(v.event, locked, { where: where || undefined, url: shareUrl, host: location.host });
      const blob = new Blob([ics], { type: "text/calendar" });
      const url = registerBlobUrl(URL.createObjectURL(blob));
      const a = el("a", { href: url, download: "event.ics" });
      document.body.append(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.remove();
      }, 1000);
    });
    const gcal = el("a", {
      class: "btn",
      href: googleCalendarUrl(v.event, locked, where || undefined),
      text: "Google Calendar",
      "data-testid": "gcal-link",
      rel: "noopener",
      target: "_blank",
    });
    const copyBtn = el("button", { type: "button", text: "Copy link", "data-testid": "copy-link" });
    copyBtn.addEventListener("click", () => {
      void (async () => {
        const result = await copyText(shareUrl, shareInput);
        copyBtn.textContent = result === "copied" ? "Copied" : "Press Ctrl+C to copy";
        setTimeout(() => {
          copyBtn.textContent = "Copy link";
        }, 2000);
      })();
    });
    actions.append(icsBtn, gcal, copyBtn);
    if (adminToken()) {
      const unlock = el("button", { type: "button", text: "Unlock", "data-testid": "unlock" });
      unlock.addEventListener("click", () => {
        void (async () => {
          const token = adminToken();
          if (!token) return;
          const res = await api("POST", `/api/events/${id}/unlock`, { body: {}, adminToken: token, parse: asStateWrap });
          if (res.ok) {
            sync = applyState(sync, res.data.state);
            updateAll();
          } else {
            org?.showError(res.error);
          }
        })().catch((e: unknown) => {
          console.error(e);
          org?.showError("Something went wrong. Try again.");
        });
      });
      actions.append(unlock);
    }
    lockedBanner.append(actions);
  }

  function updateBest(v: EventState): void {
    const locked = v.locked != null;
    bestBox.hidden = locked;
    if (locked) return;
    const wait = waitingOn(v);
    waitingLine.textContent = wait.length ? `Waiting on ${wait.length}: ${wait.join(", ")}` : "";
    clear(bestList);
    if (v.participants.length === 0) {
      bestList.append(el("p", { class: "muted", text: "No one has answered yet." }));
      return;
    }
    const ranked = getRanked(v);
    const tz = viewTz();
    ranked.forEach((w, i) => {
      const row = el("div", { class: i === 0 ? "best-row best-1" : "best-row", "data-testid": "best-row" });
      row.append(el("p", { class: "best-window", text: windowLabel(w.start, w.end, tz) }));
      const total = v.participants.length;
      const facts = [`${w.available.length} of ${total} can make it`];
      if (w.missing.length) facts.push(`Missing: ${w.missing.join(", ")}`);
      if (w.ifNeedBeCount) facts.push(`${w.ifNeedBeCount} if need be`);
      if (w.preferredCount === w.available.length && w.available.length > 0) facts.push("All preferred");
      row.append(el("p", { class: "best-facts", text: facts.join(" · ") }));
      if (adminToken()) {
        const open = el("button", { type: "button", text: "Lock this time", "data-testid": "lock-open" });
        open.addEventListener("click", () => openLock(w.start));
        row.append(open);
      }
      bestList.append(row);
    });
  }

  function openLock(slotStart: number): void {
    const v = sync.server;
    if (!v) return;
    lockSlot = slotStart;
    lockPanel.hidden = false;
    clear(lockPanel);
    const tz = viewTz();
    lockPanel.append(el("p", { text: windowLabel(slotStart, slotStart + v.event.durationMin, tz) }));
    const selects: { pollId: string; sel: HTMLSelectElement }[] = [];
    for (const poll of v.event.polls) {
      const tallies = tallyPoll(v, poll.id);
      const ties = tiedLeaders(tallies);
      const sel = el("select");
      sel.append(el("option", { value: "", text: "Choose" }));
      for (const o of poll.options) sel.append(el("option", { value: o.id, text: o.label }));
      if (ties.length === 0 && tallies[0]) sel.value = tallies[0].optionId;
      else sel.value = "";
      lockPanel.append(labeledInline(poll.question, sel));
      selects.push({ pollId: poll.id, sel });
    }
    const lockError = el("p", { class: "error", "data-testid": "lock-error" });
    const confirm = el("button", { class: "primary", type: "button", text: "Lock it", "data-testid": "lock-confirm" });
    function syncEnabled(): void {
      confirm.disabled = selects.some((s) => !s.sel.value);
    }
    for (const s of selects) s.sel.addEventListener("change", syncEnabled);
    syncEnabled();
    confirm.addEventListener("click", () => {
      void (async () => {
        lockError.textContent = "";
        const token = adminToken();
        if (!token || lockSlot === null) return;
        const pollWinners: Record<string, string> = Object.create(null) as Record<string, string>;
        for (const s of selects) pollWinners[s.pollId] = s.sel.value;
        const res = await api("POST", `/api/events/${id}/lock`, {
          body: { slotStart: lockSlot, pollWinners },
          adminToken: token,
          parse: asStateWrap,
        });
        if (res.ok) {
          lockPanel.hidden = true;
          sync = applyState(sync, res.data.state);
          updateAll();
        } else {
          lockError.textContent = res.error;
          org?.showError(res.error);
        }
      })().catch((e: unknown) => {
        console.error(e);
        lockError.textContent = "Something went wrong. Try again.";
      });
    });
    lockPanel.append(confirm, lockError);
  }

  function updatePolls(v: EventState): void {
    pollsExplain.hidden = v.event.polls.length === 0;
    const existing = new Map<string, HTMLElement>();
    for (const node of [...pollsBox.querySelectorAll("[data-poll]")]) {
      if (node instanceof HTMLElement && node.dataset.poll) existing.set(node.dataset.poll, node);
    }
    const keep = new Set(v.event.polls.map((p) => p.id));
    for (const [pid, node] of existing) {
      if (!keep.has(pid)) node.remove();
    }
    for (const poll of v.event.polls) {
      const found = existing.get(poll.id);
      const box = found ?? el("div", { class: "poll", "data-testid": "poll", "data-poll": poll.id });
      pollsBox.append(box);
      patchPoll(box, v, poll.id);
    }
  }

  function patchPoll(box: HTMLElement, v: EventState, pollId: string): void {
    const poll = v.event.polls.find((p) => p.id === pollId);
    if (!poll) return;
    const tallies = tallyPoll(v, pollId);
    const ties = new Set(tiedLeaders(tallies));
    const mine = sync.draft?.pollVotes[pollId] ?? {};
    const locked = v.locked != null;
    const headingFound = box.querySelector("h3");
    const heading = headingFound instanceof HTMLElement ? headingFound : el("h3");
    if (!headingFound) box.prepend(heading);
    heading.textContent = poll.question;
    const lockedNoteFound = box.querySelector(".poll-locked-note");
    if (locked) {
      if (!(lockedNoteFound instanceof HTMLElement)) {
        box.append(el("p", { class: "muted poll-locked-note", text: "Voting is locked." }));
      }
    } else if (lockedNoteFound instanceof HTMLElement) {
      lockedNoteFound.remove();
    }
    const keepOpts = new Set(tallies.map((t) => t.optionId));
    for (const node of [...box.querySelectorAll("[data-vote-row]")]) {
      if (node instanceof HTMLElement && node.dataset.voteRow && !keepOpts.has(node.dataset.voteRow)) node.remove();
    }
    for (const t of tallies) {
      const opt = poll.options.find((o) => o.id === t.optionId);
      const rowFound = box.querySelector(`[data-vote-row="${CSS.escape(t.optionId)}"]`);
      const row = rowFound instanceof HTMLElement ? rowFound : el("div", { class: "vote-row", "data-vote-row": t.optionId });
      box.append(row);
      const labelFound = row.querySelector(".vote-label");
      const label = labelFound instanceof HTMLElement ? labelFound : el("span", { class: "vote-label" });
      if (!labelFound) row.append(label);
      label.textContent = opt?.label ?? t.optionId;
      const tieFound = row.querySelector(".tie-mark");
      if (ties.has(t.optionId)) {
        if (!(tieFound instanceof HTMLElement)) {
          label.insertAdjacentElement("afterend", el("span", { class: "warn tie-mark", text: " Tie" }));
        }
      } else if (tieFound instanceof HTMLElement) {
        tieFound.remove();
      }
      const barFound = row.querySelector(".vote-bar");
      let bar: HTMLElement;
      let fill: HTMLElement;
      if (barFound instanceof HTMLElement) {
        bar = barFound;
        const fillFound = bar.querySelector("span");
        fill = fillFound instanceof HTMLElement ? fillFound : el("span");
        if (!fillFound) bar.append(fill);
      } else {
        fill = el("span");
        bar = el("div", { class: "vote-bar" }, fill);
        row.append(bar);
      }
      const responders = t.love + t.fine + t.no;
      const ratio = responders > 0 ? t.acceptable / responders : 0;
      fill.style.width = `${Math.round(ratio * 100)}%`;
      const buttonsFound = row.querySelector(".vote-buttons");
      const buttons = buttonsFound instanceof HTMLElement ? buttonsFound : el("div", { class: "vote-buttons" });
      if (!buttonsFound) row.append(buttons);
      for (const [label2, val] of [
        ["Love it", 2],
        ["Fine", 1],
        ["No", 0],
      ] as const) {
        const btn = ensureButton(buttons, buttons.querySelector(`button[data-vote="${val}"]`), () =>
          el("button", { type: "button", text: label2, "data-option": t.optionId, "data-vote": String(val) }),
        );
        btn.dataset.option = t.optionId;
        btn.disabled = locked || !joined();
        btn.setAttribute("aria-pressed", mine[t.optionId] === val ? "true" : "false");
        btn.onclick = () => {
          if (btn.disabled) return;
          const v2 = sync.server;
          if (!v2 || v2.locked) return;
          const chosen: PollVote = val;
          sync = edit(sync, (d) => {
            const pollVotes: Record<string, Record<string, PollVote>> = Object.create(null) as Record<
              string,
              Record<string, PollVote>
            >;
            for (const [pid2, votes] of Object.entries(d.pollVotes)) {
              const inner: Record<string, PollVote> = Object.create(null) as Record<string, PollVote>;
              for (const [oid, vv] of Object.entries(votes)) inner[oid] = vv;
              pollVotes[pid2] = inner;
            }
            const cur: Record<string, PollVote> = Object.create(null) as Record<string, PollVote>;
            for (const [oid, vv] of Object.entries(pollVotes[pollId] ?? {})) cur[oid] = vv;
            cur[t.optionId] = chosen;
            pollVotes[pollId] = cur;
            return { ...d, pollVotes };
          });
          saver.bump();
          updateAll();
        };
      }
      const countsFound = row.querySelector(".vote-counts");
      const counts = countsFound instanceof HTMLElement ? countsFound : el("span", { class: "vote-counts" });
      if (!countsFound) row.append(counts);
      counts.textContent = `${t.acceptable} can do · ${t.love} love it · ${t.no} no`;
    }
  }

  function updatePeople(v: EventState): void {
    const seen = new Set(v.participants.map((p) => p.id));
    for (const node of [...peopleList.children]) {
      if (node instanceof HTMLElement && node.dataset.pid && !seen.has(node.dataset.pid)) node.remove();
    }
    for (const p of v.participants) {
      const found = peopleList.querySelector(`li[data-pid="${CSS.escape(p.id)}"]`);
      let li: HTMLLIElement;
      if (found instanceof HTMLLIElement) {
        li = found;
      } else {
        li = el(
          "li",
          { "data-pid": p.id },
          el("strong"),
          el("span", { class: "req-flag" }),
          el("div", { class: "people-note" }),
          el("div", { class: "muted" }),
        );
      }
      peopleList.append(li);
      const strong = li.children[0];
      if (strong instanceof HTMLElement) strong.textContent = p.name;
      const reqFlag = li.children[1];
      if (reqFlag instanceof HTMLElement) reqFlag.textContent = p.required ? " required" : "";
      const note = li.children[2];
      if (note instanceof HTMLElement) {
        note.hidden = !p.note;
        note.textContent = p.note;
      }
      const updated = li.children[3];
      if (updated instanceof HTMLElement) updated.textContent = `updated ${formatUpdated(p.updatedAt, viewTz())}`;
    }

    const waiting = waitingOn(v);
    waitingHeading.hidden = waiting.length === 0;
    waitingList.hidden = waiting.length === 0;
    const wseen = new Set(waiting);
    for (const node of [...waitingList.children]) {
      if (node instanceof HTMLElement && node.dataset.name && !wseen.has(node.dataset.name)) node.remove();
    }
    for (const n of waiting) {
      const found = waitingList.querySelector(`li[data-name="${CSS.escape(n)}"]`);
      const li = found instanceof HTMLLIElement ? found : el("li", { "data-name": n, text: n });
      waitingList.append(li);
    }

    // Only while someone is still outstanding. With everyone expected accounted for, a name that is
    // not on the list is simply a guest, and saying so would be noise on every open event.
    const unexpected = waiting.length ? unmatchedAnswers(v) : [];
    unmatchedLine.hidden = unexpected.length === 0;
    unmatchedLine.textContent = unexpected.length
      ? `Answered, but not on the expected list: ${unexpected.join(", ")}`
      : "";

    const chipIds = new Set(v.participants.map((p) => p.id));
    for (const node of [...chips.children]) {
      if (node instanceof HTMLElement && node.dataset.pid && node.dataset.pid !== "*" && !chipIds.has(node.dataset.pid)) {
        node.remove();
      }
    }
    const allFound = chips.querySelector('[data-pid="*"]');
    const allChip = ensureButton(chips, allFound, () => el("button", { type: "button", text: "Everyone", "data-pid": "*" }));
    if (!allFound) {
      allChip.addEventListener("click", () => {
        filterPid = null;
        updateAll();
      });
      chips.prepend(allChip);
    }
    allChip.classList.toggle("active", filterPid === null);
    for (const p of v.participants) {
      const found = chips.querySelector(`[data-pid="${CSS.escape(p.id)}"]`);
      const chip = ensureButton(chips, found, () => el("button", { type: "button", "data-pid": p.id }));
      if (!found) {
        chip.addEventListener("click", () => {
          filterPid = p.id;
          updateAll();
        });
      }
      chip.textContent = p.name;
      chip.classList.toggle("active", filterPid === p.id);
    }
    if (filterPid && !chipIds.has(filterPid)) filterPid = null;
  }

  function updateJoin(): void {
    const gone = sync.status === "gone";
    const inPoll = joined() && !gone;
    joinBox.hidden = inPoll;
    // The button is disabled while the join form is showing and the join is on its way.
    joinSubmit.disabled = !joinBox.hidden && sync.status === "saving";
    paintBox.hidden = !inPoll;
    if (gone || sync.status === "error") joinMessage.textContent = sync.message;
    else if (!inPoll) joinMessage.textContent = "";
    if (sync.draft && shouldAssignNote(noteInput.value, sync.draft.note, document.activeElement === noteInput)) {
      noteInput.value = sync.draft.note;
    }
    noteInput.disabled = sync.status === "locked";
    fillBtn.hidden = !adminToken() || isLockedNow();
  }

  function handleGoneIfNeeded(): void {
    if (sync.status === "gone") {
      if (!clearedForGone) {
        clearJoinKeys(id);
        ident = loadIdentity(id);
        clearedForGone = true;
      }
    } else {
      clearedForGone = false;
    }
  }

  function updateAll(): void {
    handleGoneIfNeeded();
    sync = acknowledgePrune(sync);
    const v = sync.server;
    if (!v) return;
    if (filterPid && !v.participants.some((p) => p.id === filterPid)) filterPid = null;
    updateHead(v);
    updateGridInfo(v);
    updateStatus();
    updateLocked(v);
    updateJoin();
    updateHeatGrid();
    updatePaintGrid();
    updateBest(v);
    updatePolls(v);
    updatePeople(v);
    org?.update(v);
    orgDetails.hidden = !adminToken();
    renderStorageWarning();
    measureBrushHeight();
  }

  updateAll();

  return {
    destroy() {
      destroyed = true;
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("resize", scheduleResize);
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      if (shareCopyTimer) clearTimeout(shareCopyTimer);
      if (adminRecheckTimer) clearTimeout(adminRecheckTimer);
      saver.stop();
      live.stop();
      paintGrid.destroy();
      org?.destroy();
      document.documentElement.style.setProperty("--brush-h", "0px");
    },
  };
}

function labeledInline(text: string, control: HTMLElement): HTMLLabelElement {
  return el("label", { class: "field" }, el("span", { class: "label", text }), control);
}
